import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ServicesList.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getWorker, getWorkerServices } from '../../../../store/selectors';
import { salonManagementActions } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveSalonId, getActiveSalon } from '../../../../../Auth/store/selectors';
import { isDemoMode } from 'demo';
import TitleSectionBack from '../../../../../Common/TitleSectionBack';
import AvatarIcon from '../../../../../Common/AvatarIcon';
import useWindowSize from '../../../../../../utils/useWindowSize';
import clx from 'classnames';
import FilterButtons from 'Containers/Common/FilterButtons';
import { modalActions } from 'Containers/Modals/store';
import { ServiceShortItem } from './ServiceShortItem';
import ConfirmDeleteModal from 'Containers/Common/ConfirmDeleteModal';
import { Plus } from 'lucide-react';

const WorkerServices = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { workerId } = useParams();
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const [activeFilterId, setActiveFilterId] = useState(1);
  const worker = useSelector(getWorker());
  const workerServices = useSelector(getWorkerServices(activeFilterId));
  const activeSalonId = useSelector(getActiveSalonId());
  const demoMode = isDemoMode(useSelector(getActiveSalon()));
  const [activeServices, setActiveServices] = useState(worker.activeServicesIds);

  const formedServices = useMemo(() => {
    const ids = new Set((activeServices || []).map((id) => Number(id)));
    return (workerServices || [])
      .filter((service) => ids.has(Number(service.idservezio)))
      .sort((a, b) => a.position - b.position)
      .map((service) => ({
        name: service.name,
        value: service.idservezio,
        icon: service.icon,
        cost: service.prezzo,
        duration: service.time,
        onlyVisibile: service.onlyVisibile,
        id: service.idServiziPoltrona,
        position: service.position,
        disabled: service.disabled,
      }));
  }, [workerServices, activeServices]);

  const filtersButtons = [
    { name: t('workers.serviceList.filters.active'), id: 1 },
    { name: t('workers.serviceList.filters.visibleOnlyInManagement'), id: 3 },
  ];

  useEffect(() => {
    if (worker.activeServicesIds) setActiveServices(worker.activeServicesIds);
  }, [worker.activeServicesIds]);

  useEffect(() => {
    if (workerId !== 'new') {
      dispatch(salonManagementActions.GET_WORKER.REQUEST(workerId, null, { skipLoader: true }));
      dispatch(salonManagementActions.GET_SERVICES.REQUEST({}, null, { skipLoader: true }));
      dispatch(salonManagementActions.GET_WORKER_SERVICES.REQUEST(workerId, null, { skipLoader: true }));
    }
    return () => {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
      dispatch(salonManagementActions.GET_SERVICES.CLEAR());
      dispatch(salonManagementActions.GET_WORKER_SERVICES.CLEAR());
    };
  }, [workerId]);

  useEffect(() => {
    if (worker?.salone && worker?.salone !== activeSalonId) {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
      dispatch(salonManagementActions.GET_SERVICES.CLEAR());
      dispatch(salonManagementActions.GET_WORKER_SERVICES.CLEAR());
      navigate(-1);
    }
  }, [worker, activeSalonId]);

  const openJoinModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'joinServicesModal',
        props: { worker, activeServices },
      }),
    );
  };

  const addServiceButton = (
    <button type="button" className={styles.add_button} onClick={openJoinModal}>
      <Plus size={16} strokeWidth={2.4} aria-hidden />
      <span>{t('workers.serviceList.addOrRemoveService', { defaultValue: 'Aggiungi/Rimuovi servizio' })}</span>
    </button>
  );

  return (
    <div className={clx(styles.page, { [styles.page_mobile]: isMobile })}>
      <div className={styles.top_bar}>
        <TitleSectionBack
          title={t('workers.serviceList.associateServices')}
          description={t('workers.serviceList.manageServicesDescription')}
        />
        {!isMobile ? (
          <div className={styles.top_actions}>
            <div className={styles.worker_chip}>
              <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
              <div className={styles.worker_chip_copy}>
                <span>{t('workers.serviceList.modifyingServicesOf')}</span>
                <strong>{worker?.nome_referente || '—'}</strong>
              </div>
            </div>
            {demoMode ? null : addServiceButton}
          </div>
        ) : null}
      </div>

      {isMobile ? (
        <div className={styles.mobile_toolbar}>
          <div className={styles.worker_chip}>
            <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
            <div className={styles.worker_chip_copy}>
              <span>{t('workers.serviceList.modifyingServicesOf')}</span>
              <strong>{worker?.nome_referente || '—'}</strong>
            </div>
          </div>
          {demoMode ? null : addServiceButton}
        </div>
      ) : null}

      <div className={styles.toolbar}>
        <div className={styles.toolbar_copy}>
          <h3>{t('workers.serviceList.services')}</h3>
          <span>
            {formedServices.length}{' '}
            {formedServices.length === 1
              ? t('workers.serviceList.serviceCountOne')
              : t('workers.serviceList.serviceCountMany')}
          </span>
        </div>
        <div className={styles.filters_wrap}>
          <FilterButtons
            activeFilterId={activeFilterId}
            setActiveFilterId={setActiveFilterId}
            filtersButtons={filtersButtons}
          />
        </div>
      </div>

      {formedServices.length ? (
        <ServicesList services={formedServices} />
      ) : (
        <div className={styles.empty_state}>
          <p>{t('workers.serviceList.emptyList')}</p>
          {demoMode ? null : addServiceButton}
        </div>
      )}
    </div>
  );
};

const ServicesList = memo(({ services = [] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [serviceToDeleteId, setServiceToDeleteId] = useState(false);
  const [items, setItems] = useState(services);

  useEffect(() => {
    setItems(services);
  }, [services]);

  const openEditServiceModal = (service) => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'editWorkerServiceModal',
        props: service,
      }),
    );
  };

  return (
    <>
      <ConfirmDeleteModal
        isModalOpen={serviceToDeleteId}
        setIsModalOpen={setServiceToDeleteId}
        title={t('workers.serviceList.confirmDelete.title')}
        buttons={[t('common.actions.cancel'), t('common.actions.disable')]}
        action={() =>
          dispatch(
            salonManagementActions.DELETE_WORKER_SERVICE.REQUEST(
              { idServiziPoltrona: serviceToDeleteId },
              () => setServiceToDeleteId(false),
            ),
          )
        }
      />
      <div className={styles.services_grid}>
        {items.map((service) => (
          <ServiceShortItem
            key={service.value}
            service={service}
            openEditServiceModal={openEditServiceModal}
            setServiceToDeleteId={setServiceToDeleteId}
          />
        ))}
      </div>
    </>
  );
});

ServicesList.displayName = 'ServicesList';

export default WorkerServices;
